import { useEffect, useState } from 'react';
import { MobilePageHead } from '../../shell/MobilePageHead.js';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2EmptyState,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { NotificationListResponse, NotificationType } from '../../lib/types/topbar.js';
import {
  isPwaStandalone,
  isPushSupported,
  getLocalPushStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPushNotification,
  type PushStatus,
} from '../../lib/pushNotifications.js';

const TYPE_PRESENTATION: Record<
  NotificationType,
  { readonly icon: Parameters<typeof Icon>[0]['name']; readonly tone: 'info' | 'warn' | 'success' }
> = {
  reminder: { icon: 'calendar', tone: 'info' },
  invitation: { icon: 'user', tone: 'info' },
  access: { icon: 'shield', tone: 'warn' },
  subscription: { icon: 'subscription', tone: 'info' },
  operational: { icon: 'check', tone: 'success' },
};

export function NotificationsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);

  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);

  const list = useApiGet<NotificationListResponse>(
    slug ? `${base}/api/v1/notifications?limit=50` : null,
    [refreshKey],
  );

  useEffect(() => {
    void getLocalPushStatus().then(setPushStatus);
  }, []);

  if (!slug) return null;

  async function markRead(id: string, url?: string | null) {
    await apiPost(`${base}/api/v1/notifications/${id}/read`);
    setRefreshKey((k) => k + 1);
    if (url) {
      navigate(url);
    }
  }

  async function handleTogglePush() {
    if (!slug) return;
    setPushLoading(true);
    setPushMessage(null);
    try {
      if (pushStatus?.subscribed) {
        await unsubscribeFromPush(slug);
        setPushMessage('پوش نوتیفیکیشن در این دستگاه غیرفعال شد.');
      } else {
        const ok = await subscribeToPush(slug);
        if (ok) {
          setPushMessage('✅ پوش نوتیفیکیشن هوم‌اسکرین با موفقیت فعال شد!');
        } else {
          setPushMessage('⚠️ مجوز اعلان توسط مرورگر رد شد.');
        }
      }
      const updated = await getLocalPushStatus();
      setPushStatus(updated);
    } catch (err: unknown) {
      setPushMessage(`خطا: ${(err as Error).message}`);
    } finally {
      setPushLoading(false);
    }
  }

  async function handleTestPush() {
    if (!slug) return;
    setPushLoading(true);
    setPushMessage(null);
    try {
      const res = await sendTestPushNotification(slug);
      if (res.success) {
        setPushMessage(
          `🔔 اعلان آزمایشی ارسال شد! (${res.deliveredToDevices.toLocaleString('fa-IR')} دستگاه دریافت کردند)`,
        );
      } else {
        setPushMessage('ارسال ناموفق بود.');
      }
    } catch (err: unknown) {
      setPushMessage(`خطا در تست: ${(err as Error).message}`);
    } finally {
      setPushLoading(false);
    }
  }

  const isStandalone = isPwaStandalone();

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <MobilePageHead title="اعلان‌ها" />
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--sk-fg-default)' }}>
          اعلان‌ها و پوش نوتیفیکیشن
        </h1>
      </div>

      {/* PWA / Standalone Push Notification Management Card */}
      <div
        style={{
          marginTop: 16,
          marginBottom: 20,
          padding: 16,
          background: 'var(--sk-bg-elev)',
          border: '1px solid var(--sk-divider)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🔔</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              پوش نوتیفیکیشن صفحه اصلی (Add to Home Screen)
            </span>
          </div>
          {isStandalone ? (
            <V2Badge tone="success">📱 حالت هوم اسکرین فعال (PWA Standalone)</V2Badge>
          ) : (
            <V2Badge tone="neutral">🌐 مرورگر وب</V2Badge>
          )}
        </div>

        <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)', lineHeight: 1.7 }}>
          با فعال‌سازی پوش‌نوتیفیکیشن، حتی در صورتی که برنامه یا مرورگر کاملاً بسته باشد، تمام
          رویدادها و اعلان‌های اکتور-محور (مانند ارجاع وظیفه، تاییدیه خرید، سررسید چک) مستقیماً روی
          صفحه گوشی و هوم‌اسکرین شما نمایش داده می‌شوند.
        </p>

        {pushMessage ? (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              background:
                pushMessage.includes('خطا') || pushMessage.includes('⚠️')
                  ? 'var(--sk-bg-danger-subtle)'
                  : 'var(--sk-bg-success-subtle)',
              color:
                pushMessage.includes('خطا') || pushMessage.includes('⚠️')
                  ? 'var(--sk-danger)'
                  : 'var(--sk-success)',
            }}
          >
            {pushMessage}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {isPushSupported() ? (
            <>
              <V2Button
                variant={pushStatus?.subscribed ? 'secondary' : 'primary'}
                size="sm"
                onClick={handleTogglePush}
                disabled={pushLoading}
              >
                {pushLoading
                  ? 'در حال پردازش...'
                  : pushStatus?.subscribed
                    ? 'غیرفعال‌سازی اعلان‌های این دستگاه'
                    : 'فعال‌سازی دریافت اعلان در هوم‌اسکرین 📲'}
              </V2Button>

              {pushStatus?.subscribed ? (
                <V2Button
                  variant="secondary"
                  size="sm"
                  onClick={handleTestPush}
                  disabled={pushLoading}
                >
                  ارسال پوش نوتیفیکیشن آزمایشی (Test Push) 🚀
                </V2Button>
              ) : null}
            </>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
              مرورگر جاری از سرویس پوش وب پشتیبانی نمی‌کند. در iOS حتماً باید از گزینه «Add to Home
              Screen» استفاده نمایید.
            </span>
          )}
        </div>
      </div>

      {list.status === 'loading' ? <LoadingState /> : null}
      {list.status === 'error' ? (
        <StatePanel
          variant={list.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={list.error.status === 403 ? 'دسترسی ندارید' : 'اعلان‌ها قابل نمایش نیست'}
        />
      ) : null}

      {list.status === 'ready' && list.data.notifications.length === 0 ? (
        <V2EmptyState
          title="اعلانی ندارید"
          description="وقتی رویدادی توسط سایر کاربران یا سیستم برایتان ثبت شود، اینجا و روی هوم‌اسکرین نشان داده می‌شود."
        />
      ) : null}

      {list.status === 'ready' && list.data.notifications.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {list.data.notifications.map((n) => {
            const presentation = TYPE_PRESENTATION[n.type];
            return (
              <div
                key={n.notificationId}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: 14,
                  borderRadius: 12,
                  cursor: 'pointer',
                  border: '1px solid var(--sk-divider)',
                  background: n.read ? 'var(--sk-bg-canvas)' : 'var(--sk-bg-elev)',
                  boxShadow: n.read ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'background 0.2s',
                }}
                onClick={() => markRead(n.notificationId, n.target?.url)}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    flex: '0 0 auto',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `color-mix(in oklch, var(--sk-${presentation.tone}), transparent 88%)`,
                    color: `var(--sk-${presentation.tone})`,
                  }}
                >
                  <Icon name={presentation.icon} size="md" />
                </span>

                <span
                  style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{n.title}</span>
                    {n.actor?.name ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '1px 8px',
                          borderRadius: 6,
                          background: 'var(--sk-bg-subtle)',
                          color: 'var(--sk-fg-muted)',
                        }}
                      >
                        👤 {n.actor.name}
                        {n.actor.verb ? ` • ${n.actor.verb}` : ''}
                      </span>
                    ) : null}
                    {n.target?.entityRef ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontFamily: 'monospace',
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: 'var(--sk-brand-50)',
                          color: 'var(--sk-brand-700)',
                        }}
                      >
                        {n.target.entityRef}
                      </span>
                    ) : null}
                  </div>

                  <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)', lineHeight: 1.6 }}>
                    {n.body}
                  </span>

                  {n.target?.url ? (
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--sk-brand-600)',
                        marginTop: 2,
                        fontWeight: 500,
                      }}
                    >
                      برای مشاهده جزئیات کلیک کنید ←
                    </span>
                  ) : null}
                </span>

                {!n.read ? (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--sk-danger)',
                      flex: '0 0 auto',
                      marginTop: 6,
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
